<section class="ai-section">
    <div class="container">
        <div class="card">
            <div class="page-header">
                <div>
                    <h3 class="page-title">Project plans</h3>
                    <p class="page-subtitle">
                        Limits per project for the agents customers create in their own Dashboard: number of agents,
                        messages per month, allowed models, and the features images, documents, voice, knowledge files, playbooks, tools (the agent's AI employee calls the project's services, e.g. Leads) and automations (timed tasks the agent runs by itself).
                        A project without a plan uses the defaults ({{ defaults.maxAgents }} agents,
                        {{ defaults.maxMessagesPerMonth }} messages per month, all enabled models, every feature allowed,
                        {{ defaults.maxKnowledgeFiles }} knowledge files / {{ defaults.maxKnowledgeMb }} MB,
                        {{ defaults.maxAudioSecondsPerMonth }} audio seconds per month, {{ defaults.maxPlaybooksPerAgent }} playbooks per agent, {{ defaults.maxToolConnectionsPerAgent }} tool connections per agent,
                        {{ defaults.maxTriggersPerAgent }} automations per agent, {{ defaults.maxTriggerRunsPerMonth }} automation runs per month).
                    </p>
                </div>
            </div>

            <div class="toolbar">
                <div class="form-field">
                    <label for="pl-search">Search</label>
                    <input id="pl-search" [(ngModel)]="search" placeholder="Project name or ID" dir="auto" />
                </div>
            </div>

            <div *ngIf="loadError" class="form-field"><span class="error-text">{{ loadError }}</span></div>

            <div class="table-wrapper table-mobile-card">
                <table class="table" id="ai-project-plans-table">
                    <thead>
                        <tr>
                            <th>Project</th>
                            <th>Max agents</th>
                            <th>Messages / month</th>
                            <th>Allowed models</th>
                            <th>Features</th>
                            <th>Knowledge files / MB</th>
                            <th>Audio s / month</th>
                            <th>Playbooks / agent</th>
                            <th>Tool connections / agent</th>
                            <th>Automations / agent · runs / month</th>
                            <th class="status-col">Enabled</th>
                            <th class="date-col">Updated</th>
                            <th class="actions-wide">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngIf="!loading && filtered.length === 0">
                            <td colspan="100%">
                                <div class="no-data"><i class="fa-solid fa-sliders"></i> No projects found</div>
                            </td>
                        </tr>
                        <tr *ngFor="let r of filtered">
                            <td data-label="Project">
                                <strong dir="auto">{{ r.projectName }}</strong>
                                <div class="muted mono">{{ r.projectId }}</div>
                            </td>
                            <td data-label="Max agents">{{ r.plan.maxAgents }}</td>
                            <td data-label="Messages / month">{{ r.plan.maxMessagesPerMonth }}</td>
                            <td data-label="Allowed models">{{ modelsLabel(r.plan) }}</td>
                            <td data-label="Features">{{ featuresLabel(r.plan) }}</td>
                            <td data-label="Knowledge files / MB">{{ r.plan.maxKnowledgeFiles }} / {{ r.plan.maxKnowledgeMb }}</td>
                            <td data-label="Audio s / month">{{ r.plan.maxAudioSecondsPerMonth }}</td>
                            <td data-label="Playbooks / agent">{{ r.plan.allowPlaybooks ? r.plan.maxPlaybooksPerAgent : 'Off' }}</td>
                            <td data-label="Tool connections / agent">{{ r.plan.allowTools ? r.plan.maxToolConnectionsPerAgent : 'Off' }}</td>
                            <td data-label="Automations / agent · runs / month">{{ r.plan.allowTriggers ? r.plan.maxTriggersPerAgent + ' · ' + r.plan.maxTriggerRunsPerMonth : 'Off' }}</td>
                            <td data-label="Enabled" class="status-col">
                                <span [ngClass]="r.plan.enabled ? 'status-active' : 'status-disabled'">{{ r.plan.enabled ? 'Yes' : 'No' }}</span>
                                <span *ngIf="r.isDefault" class="badge draft" title="No stored plan: the defaults apply">Default</span>
                            </td>
                            <td data-label="Updated" class="date-col">{{ r.plan.updatedAt ? (r.plan.updatedAt | date: 'MMM d, y') : '-' }}</td>
                            <td data-label="Actions" class="actions-wide">
                                <div class="actions-cell">
                                    <button *ngIf="perms.has(P.planUpdate)" class="icon-btn edit" title="Edit plan" (click)="openEdit(r)">
                                        <i class="fa-solid fa-pen"></i>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</section>

<div *ngIf="editing" class="dialog-overlay">
    <div class="dialog-card" role="dialog" aria-modal="true">
        <div class="dialog-header">
            <div>
                <h3>Edit plan</h3>
                <p dir="auto">{{ editing.projectName }} <span class="mono">({{ editing.projectId }})</span></p>
            </div>
            <button type="button" class="icon-btn back" (click)="closeEditor()"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <form class="dialog-body" (ngSubmit)="save()">
            <div class="two-col">
                <div class="form-field">
                    <label for="pl-agents">Max agents</label>
                    <input id="pl-agents" name="maxAgents" type="number" min="0" step="1" [(ngModel)]="form.maxAgents" />
                </div>
                <div class="form-field">
                    <label for="pl-msgs">Max messages per month</label>
                    <input id="pl-msgs" name="maxMessagesPerMonth" type="number" min="0" step="1" [(ngModel)]="form.maxMessagesPerMonth" />
                    <span class="hint">Assistant answers over every channel. The system test chat is not counted.</span>
                </div>
            </div>

            <div class="form-field">
                <label>Allowed models</label>
                <label class="check-row">
                    <input type="radio" name="allModels" [value]="true" [(ngModel)]="form.allModels" />
                    All enabled models
                </label>
                <label class="check-row">
                    <input type="radio" name="allModels" [value]="false" [(ngModel)]="form.allModels" />
                    Only the selected models
                </label>
            </div>

            <div *ngIf="!form.allModels" class="form-field">
                <span *ngIf="modelGroups.length === 0" class="hint">No models available. Add or enable models on the LLM Models page.</span>
                <div *ngFor="let g of modelGroups">
                    <div class="section-title" style="font-size: 14px; margin-top: 6px">{{ g.label }}</div>
                    <label *ngFor="let m of g.models" class="check-row">
                        <input
                            type="checkbox"
                            [name]="'model-' + m.id"
                            [checked]="form.selected.has(m.id)"
                            (change)="toggleModel(m.id, $any($event.target).checked)"
                        />
                        {{ m.label }}
                    </label>
                </div>
            </div>

            <div class="form-field">
                <label>Features</label>
                <span class="hint">Part of the Web Studio version, so allowed by default; switch one off only as a support exception. Playbooks have no per-agent switch: each playbook's own "enabled" is the switch. Tools are switched on per service in each agent by the customer. Automations are created by the customer; their runs also count as messages.</span>
                <div class="copy-row plan-features">
                    <label class="check-row" *ngFor="let f of features">
                        <input type="checkbox" [name]="'feature-' + f.key" [id]="'pl-' + f.key" [ngModel]="form[f.key]" (ngModelChange)="setFeature(f.key, $event)" />
                        {{ f.label }}
                    </label>
                </div>
            </div>

            <div class="three-col">
                <div class="form-field" *ngFor="let l of limits">
                    <label [for]="'pl-' + l.key">{{ l.label }}</label>
                    <input [id]="'pl-' + l.key" [name]="l.key" type="number" min="0" step="1" [(ngModel)]="form[l.key]" />
                    <span class="hint">{{ l.hint }}</span>
                </div>
            </div>

            <label class="check-row">
                <input type="checkbox" name="enabled" [(ngModel)]="form.enabled" />
                Enabled (when off, the agents of this project do not answer)
            </label>

            <div *ngIf="formError" class="form-field"><span class="error-text">{{ formError }}</span></div>
            <div class="dialog-footer">
                <button type="button" class="btn-light" (click)="closeEditor()">Cancel</button>
                <button type="submit" class="btn-primary" [disabled]="saving">Save</button>
            </div>
        </form>
    </div>
</div>
